import PropTypes from 'prop-types';
import { useState } from 'react';

import { GoogleLogin } from '@react-oauth/google';

import { Box, Typography, useTheme, withStyles } from '@material-ui/core/';
import FacebookIcon from '@material-ui/icons/Facebook';

import { Button, IconButton, CheckBox, PasswordInput, TextInput } from 'evee-ui-common/components/base';

import Dialog from '../../CommonDialog';
import styles from './styles';

const SignIn = ({
  isMobile,
  loading,
  signIn,
  classes,
  onSignIn,
  onSignUp,
  onClose,
  onFacebookSignIn,
  onFieldChanged,
  onForgotPassword,
  onGoogleSignIn,
}) => {
  const theme = useTheme();
  const [formatError, setFormatError] = useState(null);
  const { email, password, open, errorMessage, rememberMe } = signIn;

  const actions = () => (
    <>
      <Typography className={classes.footerText}>Don&apos;t have an account yet?</Typography>
      <Button text="Sign Up" color="primary" type="outlined" onClick={onSignUp} />
    </>
  );

  const emailError = !errorMessage?.includes('password') ? errorMessage : formatError || '';
  const passwordError = errorMessage?.includes('password') ? errorMessage : '';
  const canSignIn = email && password && !formatError;

  const onEnter = () => {
    if (!canSignIn) {
      return;
    }

    onSignIn();
  };

  return (
    <Dialog
      decorateBackground
      open={open}
      isMobile={isMobile}
      data-test-id="log-in-dialog"
      title="Log in"
      loading={loading}
      actions={actions}
      onClose={onClose}
    >
      <TextInput
        value={email}
        label="E-mail"
        style={{ marginBottom: '15px' }}
        helperText={emailError}
        error={!!emailError}
        format="email"
        name="email"
        onChange={(v) => onFieldChanged('email', v)}
        onEnter={onEnter}
        onFormatError={(e) => setFormatError(e)}
      />

      <PasswordInput
        value={password}
        label="Password"
        helperText={passwordError}
        error={!!passwordError}
        name="password"
        onChange={(v) => onFieldChanged('password', v)}
        onEnter={onEnter}
      />

      <Box
        style={{ margin: '8px 0' }}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
      >
        <CheckBox
          checked={rememberMe}
          label="Remember me"
          onChange={(v) => onFieldChanged('rememberMe', v)}
        />
        <Button
          text="Forgot password?"
          type="text"
          color="primary"
          className={classes.forgotButton}
          onClick={onForgotPassword}
        />
      </Box>

      <Button text="Log in" disabled={!canSignIn} onClick={onSignIn} />

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        className={classes.socialButtonsContainer}
      >
        <Typography className={classes.socialsText}>Or log in with:</Typography>
        <IconButton
          icon={<FacebookIcon className={classes.facebookIcon} />}
          className={classes.socialButton}
          style={{
            color: theme.palette.common.white,
            backgroundColor: theme.palette.common.navyBlue,
          }}
          onClick={onFacebookSignIn}
        />
        <Box className={classes.socialButton}>
          <GoogleLogin
            use_fedcm_for_button
            size="large"
            type="icon"
            theme="filled_blue"
            shape="circle"
            onSuccess={(credentialResponse) => {
              onGoogleSignIn(credentialResponse.credential);
            }}
            onError={() => {
              console.log('Login Failed');
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

SignIn.propTypes = {
  signIn: PropTypes.object,
  isMobile: PropTypes.bool,
  loading: PropTypes.bool,
  classes: PropTypes.shape({
    forgotButton: PropTypes.string.isRequired,
    footerText: PropTypes.string.isRequired,
    socialsText: PropTypes.string.isRequired,
    socialButtonsContainer: PropTypes.string.isRequired,
    socialButton: PropTypes.string.isRequired,
    facebookIcon: PropTypes.string.isRequired,
  }).isRequired,
  onSignUp: PropTypes.func,
  onSignIn: PropTypes.func,
  onClose: PropTypes.func,
  onFacebookSignIn: PropTypes.func,
  onFieldChanged: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onGoogleSignIn: PropTypes.func,
};

SignIn.defaultProps = {
  signIn: {},
  isMobile: false,
  loading: false,
  onSignUp: () => {},
  onSignIn: () => {},
  onClose: () => {},
  onFacebookSignIn: () => {},
  onFieldChanged: () => {},
  onForgotPassword: () => {},
  onGoogleSignIn: () => {},
};

export default withStyles(styles)(SignIn);
